import { Eye, File, FileSpreadsheet, FileText, Upload, Wifi, WifiOff } from 'lucide-react';

import { DisciplinaInfo } from '@features/midia/hooks/useDocumentosObra';

interface DocumentGalleryProps {
  disciplinas: DisciplinaInfo[];
  onDisciplinaClick: (disciplinaId: string) => void;
  onDocumentoClick?: (disciplinaId: string, documentoId: string) => void; // ✅ Para clique direto em documento
  onUploadClick: (disciplinaId: string) => void;
  getDocumentosPorDisciplina?: (disciplinaId: string) => any[]; // ✅ Para acessar documentos
  modoEscuro?: boolean;
  loading?: boolean;
  // ✅ NOVOS PROPS PARA SISTEMA OFFLINE
  isFromCache?: boolean;
  isOnline?: boolean;
}

export default function DocumentGallery({ 
  disciplinas, 
  onDisciplinaClick,
  onDocumentoClick, // ✅ Para clique direto em documento
  onUploadClick,
  getDocumentosPorDisciplina, // ✅ Para acessar documentos
  modoEscuro = false,
  loading = false,
  // ✅ NOVOS PROPS PARA SISTEMA OFFLINE
  isFromCache = false,
  isOnline = true
}: DocumentGalleryProps) {
  
  // ✅ CORES ATUALIZADAS COM ORÇAMENTO E CRONOGRAMA
  const getCorPorDisciplina = (cor: string, modoEscuro: boolean) => {
    const cores = {
      blue: modoEscuro ? 'from-blue-900/20 to-blue-800/10 border-blue-800/30' : 'from-blue-50 to-blue-100/50 border-blue-200',
      orange: modoEscuro ? 'from-orange-900/20 to-orange-800/10 border-orange-800/30' : 'from-orange-50 to-orange-100/50 border-orange-200',
      cyan: modoEscuro ? 'from-cyan-900/20 to-cyan-800/10 border-cyan-800/30' : 'from-cyan-50 to-cyan-100/50 border-cyan-200',
      yellow: modoEscuro ? 'from-yellow-900/20 to-yellow-800/10 border-yellow-800/30' : 'from-yellow-50 to-yellow-100/50 border-yellow-200',
      red: modoEscuro ? 'from-red-900/20 to-red-800/10 border-red-800/30' : 'from-red-50 to-red-100/50 border-red-200',
      purple: modoEscuro ? 'from-purple-900/20 to-purple-800/10 border-purple-800/30' : 'from-purple-50 to-purple-100/50 border-purple-200',
      green: modoEscuro ? 'from-green-900/20 to-green-800/10 border-green-800/30' : 'from-green-50 to-green-100/50 border-green-200', // ✅ Para orçamento
      gray: modoEscuro ? 'from-gray-900/20 to-gray-800/10 border-gray-800/30' : 'from-gray-50 to-gray-100/50 border-gray-200',
    };
    return cores[cor as keyof typeof cores] || cores.gray;
  };

  const getCorTexto = (cor: string, modoEscuro: boolean) => {
    if (modoEscuro) return 'text-gray-100';
    
    const cores = {
      blue: 'text-blue-800',
      orange: 'text-orange-800', 
      cyan: 'text-cyan-800',
      yellow: 'text-yellow-800',
      red: 'text-red-800',
      purple: 'text-purple-800',
      green: 'text-green-800', // ✅ Para orçamento
      gray: 'text-gray-800',
    };
    return cores[cor as keyof typeof cores] || cores.gray;
  };

  // ✅ FUNÇÃO PARA DETECTAR SE DOCUMENTO É EXCEL
  const isExcelDocument = (documento: any): boolean => {
    if (!documento) return false;
    return documento.tipo_detectado === 'excel' ||
           documento.nome_arquivo?.toLowerCase().includes('.xlsx') ||
           documento.nome_arquivo?.toLowerCase().includes('.xls') ||
           (documento.tipo_arquivo && (
             documento.tipo_arquivo.includes('spreadsheet') ||
             documento.tipo_arquivo.includes('excel')
           ));
  };

  // ✅ FUNÇÃO PARA OBTER PRIMEIRO DOCUMENTO DA DISCIPLINA
  const getPrimeiroDocumento = (disciplinaId: string) => {
    if (!getDocumentosPorDisciplina) return null;
    const documentos = getDocumentosPorDisciplina(disciplinaId);
    // Ordenar por data de criação (mais recente primeiro) e pegar o primeiro
    return documentos
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] || null;
  };

  // ✅ FUNÇÃO PARA OBTER ÍCONE DO DOCUMENTO
  const getIconeDocumento = (documento: any) => {
    if (!documento) return <File className="w-4 h-4 text-gray-500" />;
    
    if (isExcelDocument(documento)) {
      return <FileSpreadsheet className="w-4 h-4 text-green-600" />;
    }
    return <FileText className="w-4 h-4 text-red-500" />;
  };

  // ✅ FUNÇÃO PARA OBTER ESTATÍSTICAS DOS TIPOS DE ARQUIVO
  const getEstatisticasTipos = (disciplinaId: string) => {
    if (!getDocumentosPorDisciplina) return { pdfs: 0, excels: 0 };
    
    const documentos = getDocumentosPorDisciplina(disciplinaId);
    const pdfs = documentos.filter(doc => !isExcelDocument(doc)).length;
    const excels = documentos.filter(doc => isExcelDocument(doc)).length;
    
    return { pdfs, excels };
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 9 }).map((_, index) => (
          <div
            key={index}
            className={`p-6 rounded-lg border animate-pulse ${
              modoEscuro ? 'bg-gray-700 border-gray-600' : 'bg-gray-200 border-gray-300'
            }`}
          >
            <div className={`h-6 rounded mb-4 ${modoEscuro ? 'bg-gray-600' : 'bg-gray-300'}`} />
            <div className={`h-4 rounded mb-2 ${modoEscuro ? 'bg-gray-600' : 'bg-gray-300'}`} />
            <div className={`h-4 rounded w-3/4 ${modoEscuro ? 'bg-gray-600' : 'bg-gray-300'}`} />
          </div>
        ))}
      </div>
    );
  }

  const disciplinasComDocumentos = disciplinas.filter(d => d.total_documentos > 0);
  const disciplinasSemDocumentos = disciplinas.filter(d => d.total_documentos === 0);

  if (disciplinasComDocumentos.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center py-16 px-4 ${
        modoEscuro ? 'text-gray-400' : 'text-gray-500'
      }`}>
        <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 ${
          modoEscuro ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <FileText className="w-12 h-12" />
        </div>
        <h3 className="text-lg font-ageo-bold mb-2">
          {isFromCache ? 'Nenhum documento no cache' : 'Nenhum documento encontrado'}
        </h3>
        <p className="text-sm font-ageo-regular text-center max-w-md mb-6">
          {isFromCache 
            ? 'Conecte-se à internet para carregar os documentos da obra.'
            : 'Comece fazendo upload dos projetos da obra organizados por disciplina.'
          }
        </p>
        
        {/* ✅ BOTÃO CONDICIONAL BASEADO NO STATUS ONLINE */}
        {isOnline ? (
          <button
            onClick={() => onUploadClick('arquitetonico')}
            className={`px-6 py-3 rounded-md font-ageo-regular transition-colors flex items-center gap-2 ${
              modoEscuro 
                ? "bg-green-600 text-white hover:bg-green-700" 
                : "bg-confirmation text-white hover:bg-confirmation/90"
            }`}
          >
            <Upload className="w-4 h-4" />
            Fazer Primeiro Upload
          </button>
        ) : (
          <div className={`px-6 py-3 rounded-md font-ageo-regular flex items-center gap-2 ${
            modoEscuro 
              ? "bg-gray-600 text-gray-400" 
              : "bg-gray-300 text-gray-500"
          }`}>
            <WifiOff className="w-4 h-4" />
            Upload indisponível offline
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">


      {/* Disciplinas com documentos */}
      {disciplinasComDocumentos.length > 0 && (
        <div>
          <h3 className={`text-lg font-ageo-bold mb-4 flex items-center gap-2 ${
            modoEscuro ? 'text-gray-100' : 'text-foreground'
          }`}>
            Disciplinas com Projetos
            {/* ✅ INDICADOR DE CACHE/OFFLINE */}
            {isFromCache && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                modoEscuro ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
              }`}>
                Cache
              </span>
            )}
            {!isOnline && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                modoEscuro ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
              }`}>
                Offline
              </span>
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {disciplinasComDocumentos.map((disciplina) => {
              const primeiroDocumento = getPrimeiroDocumento(disciplina.id);
              const stats = getEstatisticasTipos(disciplina.id);
              
              return (
                <div
                  key={disciplina.id}
                  className={`group relative rounded-lg border p-6 transition-all duration-200 hover:shadow-lg bg-gradient-to-br ${getCorPorDisciplina(disciplina.cor, modoEscuro)} ${
                    !isOnline ? 'opacity-80' : ''
                  }`}
                >
                  {/* ✅ INDICADOR DE STATUS NO CANTO */}
                  {(isFromCache || !isOnline) && (
                    <div className="absolute top-2 right-2">
                      <div className={`p-1 rounded-full ${
                        !isOnline 
                          ? (modoEscuro ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700')
                          : (modoEscuro ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700')
                      }`}>
                        {!isOnline ? <WifiOff className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                      </div>
                    </div>
                  )}

                  {/* Header da disciplina */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{disciplina.icon}</span>
                      <div>
                        <h4 className={`font-ageo-bold text-base ${getCorTexto(disciplina.cor, modoEscuro)}`}>
                          {disciplina.nome}
                        </h4>
                        {disciplina.versao_atual && (
                          <span className={`text-sm font-ageo-regular ${
                            modoEscuro ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {disciplina.versao_atual}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ✅ INFORMAÇÕES ATUALIZADAS COM TIPOS DE ARQUIVO */}
                  <div className="space-y-2 mb-4">
                    <div className={`text-sm font-ageo-regular ${
                      modoEscuro ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {disciplina.total_documentos} documento{disciplina.total_documentos !== 1 ? 's' : ''}
                    </div>
                    
                    {/* ✅ BREAKDOWN POR TIPO DE ARQUIVO */}
                    {(stats.pdfs > 0 || stats.excels > 0) && (
                      <div className="flex items-center gap-3 text-xs">
                        {stats.pdfs > 0 && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3 h-3 text-red-500" />
                            <span className={modoEscuro ? 'text-gray-400' : 'text-gray-500'}>
                              {stats.pdfs} PDF{stats.pdfs !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                        {stats.excels > 0 && (
                          <div className="flex items-center gap-1">
                            <FileSpreadsheet className="w-3 h-3 text-green-600" />
                            <span className={modoEscuro ? 'text-gray-400' : 'text-gray-500'}>
                              {stats.excels} Excel{stats.excels !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {disciplina.ultima_atualizacao && (
                      <div className={`text-xs font-ageo-regular ${
                        modoEscuro ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        Atualizado {new Date(disciplina.ultima_atualizacao).toLocaleDateString('pt-BR')}
                      </div>
                    )}
                  </div>

                  {/* ✅ PREVIEW DO PRIMEIRO DOCUMENTO (SE EXISTIR) */}
                  {primeiroDocumento && (
                    <div className={`mb-4 p-3 rounded-md border-l-4 ${
                      modoEscuro 
                        ? 'bg-gray-700/50 border-l-gray-500' 
                        : 'bg-white/80 border-l-gray-400'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        {getIconeDocumento(primeiroDocumento)}
                        <span className={`text-xs font-ageo-bold truncate ${
                          modoEscuro ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          {primeiroDocumento.nome_arquivo}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`text-xs font-ageo-regular ${
                          modoEscuro ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Último documento • {primeiroDocumento.versao}
                        </div>
                        {/* ✅ TAG DO TIPO DE ARQUIVO */}
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          isExcelDocument(primeiroDocumento)
                            ? (modoEscuro ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700')
                            : (modoEscuro ? 'bg-red-900/30 text-red-400' : 'bg-red-100 text-red-700')
                        }`}>
                          {isExcelDocument(primeiroDocumento) ? 'Excel' : 'PDF'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Ações */}
                  <div className="flex gap-2">
                    {/* ✅ BOTÃO DE ABERTURA RÁPIDA (PRIMEIRO DOCUMENTO) */}
                    {primeiroDocumento && onDocumentoClick ? (
                      <button
                        onClick={() => onDocumentoClick(disciplina.id, primeiroDocumento.id)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-ageo-regular transition-colors flex items-center justify-center gap-2 ${
                          modoEscuro 
                            ? 'bg-blue-600 text-white hover:bg-blue-700' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        title="Abrir último documento diretamente"
                      >
                        <Eye className="w-4 h-4" />
                        Abrir
                      </button>
                    ) : (
                      <button
                        onClick={() => onDisciplinaClick(disciplina.id)}
                        className={`flex-1 px-3 py-2 rounded-md text-sm font-ageo-regular transition-colors flex items-center justify-center gap-2 ${
                          modoEscuro 
                            ? 'bg-gray-700/50 text-gray-100 hover:bg-gray-700' 
                            : 'bg-white/80 text-gray-700 hover:bg-white'
                        }`}
                      >
                        <Eye className="w-4 h-4" />
                        Ver
                      </button>
                    )}
                    
                    {/* Botão Ver Todos (quando há documento para abertura rápida) */}
                    {primeiroDocumento && onDocumentoClick && (
                      <button
                        onClick={() => onDisciplinaClick(disciplina.id)}
                        className={`px-3 py-2 rounded-md text-sm font-ageo-regular transition-colors flex items-center gap-2 ${
                          modoEscuro 
                            ? 'bg-gray-700/50 text-gray-100 hover:bg-gray-700' 
                            : 'bg-white/80 text-gray-700 hover:bg-white'
                        }`}
                        title="Ver todos os documentos da disciplina"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Todos</span>
                      </button>
                    )}
                    
                    {/* ✅ BOTÃO DE UPLOAD CONDICIONAL */}
                    {isOnline ? (
                      <button
                        onClick={() => onUploadClick(disciplina.id)}
                        className={`px-3 py-2 rounded-md text-sm font-ageo-regular transition-colors flex items-center gap-2 ${
                          modoEscuro 
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-confirmation text-white hover:bg-confirmation/90'
                        }`}
                      >
                        <Upload className="w-4 h-4" />
                        <span className="hidden sm:inline">Upload</span>
                      </button>
                    ) : (
                      <div className={`px-3 py-2 rounded-md text-sm font-ageo-regular flex items-center gap-2 cursor-not-allowed ${
                        modoEscuro 
                          ? 'bg-gray-600 text-gray-400' 
                          : 'bg-gray-300 text-gray-500'
                      }`}
                      title="Upload não disponível offline">
                        <WifiOff className="w-4 h-4" />
                        <span className="hidden sm:inline">Offline</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Disciplinas sem documentos */}
      {disciplinasSemDocumentos.length > 0 && (
        <div>
          <h3 className={`text-lg font-ageo-bold mb-4 ${
            modoEscuro ? 'text-gray-100' : 'text-foreground'
          }`}>
            Outras Disciplinas
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {disciplinasSemDocumentos.map((disciplina) => (
              <button
                key={disciplina.id}
                onClick={() => isOnline ? onUploadClick(disciplina.id) : undefined}
                disabled={!isOnline}
                className={`group p-4 rounded-lg border transition-all duration-200 hover:shadow-md bg-gradient-to-br ${getCorPorDisciplina(disciplina.cor, modoEscuro)} ${
                  !isOnline ? 'opacity-40 cursor-not-allowed' : 'opacity-60 hover:opacity-100 cursor-pointer'
                }`}
                title={!isOnline ? 'Upload não disponível offline' : 'Fazer upload de documentos'}
              >
                <div className="flex flex-col items-center text-center">
                  <span className="text-xl mb-2">{disciplina.icon}</span>
                  <h4 className={`font-ageo-bold text-sm ${getCorTexto(disciplina.cor, modoEscuro)} mb-1`}>
                    {disciplina.nome}
                  </h4>
                  <div className={`text-xs font-ageo-regular ${
                    modoEscuro ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Sem projetos
                  </div>
                  {isOnline ? (
                    <Upload className="w-4 h-4 mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                  ) : (
                    <WifiOff className="w-4 h-4 mt-2 opacity-50" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}